/**
 * Calendar Event Edit Page
 * Wrapper page for editing an existing event
 */

import { useEffect } from 'react'
import { Link, useParams, useNavigate, useLocation } from 'react-router-dom'
import {
  Box,
  Container,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Chip,
  Stack,
} from '@mui/material'
import { ArrowBack } from '@mui/icons-material'
import CalendarEventEditor from '../components/editor/CalendarEventEditor'
import { useCalendarEventForEdit } from '../hooks/useCalendarEvents'
import {
  useCalendarEventEditorStore,
  selectIsEventPublished,
} from '../stores/calendarEventEditorStore'
import {
  getEventStatusLabel,
  getEventStatusColor,
} from '@api/types/calendarEvent'
import {
  CalendarEventSectionId,
  VALID_SECTION_IDS,
} from '../types/calendar-event-editor.types'

export function CalendarEventEditPage() {
  const { eventId } = useParams<{ eventId: string }>()
  const navigate = useNavigate()
  const location = useLocation()

  const { data: event, isLoading, isError } = useCalendarEventForEdit(eventId || '')
  const setActiveSection = useCalendarEventEditorStore((state) => state.setActiveSection)

  // Handle URL hash for section navigation
  useEffect(() => {
    const hash = location.hash.replace('#', '')
    if (hash && VALID_SECTION_IDS.includes(hash as CalendarEventSectionId)) {
      setActiveSection(hash as CalendarEventSectionId)
    }
  }, [location.hash, setActiveSection])

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (isError || !event) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">
          Event not found or you don't have permission to edit it.
        </Alert>
        <Button
          component={Link}
          to="/events/my-events"
          startIcon={<ArrowBack />}
          sx={{ mt: 2 }}
        >
          Back to My Events
        </Button>
      </Container>
    )
  }

  // Check edit permission
  if (event.canEdit === false) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">
          You don't have permission to edit this event.
        </Alert>
        <Button
          component={Link}
          to={`/events/${eventId}`}
          startIcon={<ArrowBack />}
          sx={{ mt: 2 }}
        >
          View Event
        </Button>
      </Container>
    )
  }

  return (
    <Box>
      <Container maxWidth="md" sx={{ py: 2 }}>
        {/* Navigation */}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          spacing={2}
          sx={{ mb: 2 }}
        >
          <Button
            component={Link}
            to={`/events/${eventId}`}
            startIcon={<ArrowBack />}
            color="inherit"
          >
            Back to Event
          </Button>

          {/* Status chip */}
          <Chip
            label={getEventStatusLabel(event.status)}
            color={getEventStatusColor(event.status)}
          />
        </Stack>

        <Typography variant="h4" component="h1" gutterBottom>
          Edit Event
        </Typography>

        <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
          {event.title}
        </Typography>
      </Container>

      {/* Editor */}
      <CalendarEventEditor event={event} />
    </Box>
  )
}

export default CalendarEventEditPage
